import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';

const UserProfileDropdown = ({ userType, onLogout, isMobile = false, className = '', userId = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const mockUser = {
    name: 'Juan Dela Cruz',
    email: 'juan.delacruz@email.com',
    avatar: '/assets/images/avatar-placeholder.jpg',
    isVerified: true,
    profileCompletion: 85,
  };

  const providerMenuItems = [
    { label: 'My Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'My Applications', path: '/user-dashboard', icon: 'FileText' },
    { label: 'Messages', path: '/user-dashboard', icon: 'MessageSquare', badge: 3 },
    { label: 'My Profile', path: '/user-dashboard', icon: 'User' },
    { label: 'Settings', path: '/user-dashboard', icon: 'Settings' },
  ];

  const seekerMenuItems = [
    { label: 'My Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Posted Jobs', path: '/user-dashboard', icon: 'Briefcase' },
    { label: 'Messages', path: '/user-dashboard', icon: 'MessageSquare', badge: 5 },
    { label: 'Saved Workers', path: '/user-dashboard', icon: 'Heart' },
    { label: 'Settings', path: '/user-dashboard', icon: 'Settings' },
  ];

  const menuItems = userType === 'provider' ? providerMenuItems : seekerMenuItems;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout();
  };

  if (isMobile) {
    return (
      <div className="space-y-[6px]">
        <div className="flex items-center gap-[12px] px-[18px] py-[12px] bg-muted rounded-md">
          <div className="relative">
            <Image
              src={mockUser?.avatar}
              alt={mockUser?.name}
              className="w-[40px] h-[40px] rounded-full object-cover"
            />
            {mockUser?.isVerified && (
              <div className="absolute -bottom-[2px] -right-[2px] w-[16px] h-[16px] bg-success rounded-full border-2 border-card flex items-center justify-center">
                <Icon name="Check" size={10} color="white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate">{mockUser?.name}</p>
            <p className="text-[13px] text-muted-foreground truncate">{mockUser?.email}</p>
          </div>
        </div>
        {menuItems?.map((item) => (
          <Link
            key={item?.path}
            to={item?.path}
            className="flex items-center justify-between px-[18px] py-[12px] rounded-md
              text-foreground hover:bg-muted transition-smooth"
          >
            <div className="flex items-center gap-[12px]">
              <Icon name={item?.icon} size={18} />
              <span className="font-medium text-[15px]">{item?.label}</span>
            </div>
            {item?.badge && (
              <span className="px-[8px] py-[2px] bg-accent text-accent-foreground text-[12px] font-medium rounded-full">
                {item?.badge}
              </span>
            )}
          </Link>
        ))}
        <button
          onClick={handleLogoutClick}
          className="w-full flex items-center gap-[12px] px-[18px] py-[12px] rounded-md
            text-error hover:bg-error/10 transition-smooth font-medium text-[15px]"
        >
          <Icon name="LogOut" size={18} />
          <span>Logout</span>
        </button>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-[8px] p-[6px] rounded-md hover:bg-muted transition-smooth"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div className="relative">
          <Image
            src={mockUser?.avatar}
            alt={mockUser?.name}
            className="w-[36px] h-[36px] rounded-full object-cover"
          />
          {mockUser?.isVerified && (
            <div className="absolute -bottom-[2px] -right-[2px] w-[14px] h-[14px] bg-success rounded-full border-2 border-card flex items-center justify-center">
              <Icon name="Check" size={8} color="white" />
            </div>
          )}
        </div>
        <Icon name="ChevronDown" size={16} className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-[280px] bg-card rounded-md shadow-warm-lg border border-border z-dropdown animate-in fade-in slide-in-from-top-2 duration-250">
          <Link 
            to={userId ? `/worker-profile?workerId=${userId}` : '/worker-profile'}
            onClick={() => setIsOpen(false)}
            className="block p-[18px] border-b border-border hover:bg-muted/50 transition-smooth cursor-pointer"
          >
            <div className="flex items-center gap-[12px] mb-[12px]">
              <div className="relative">
                <Image
                  src={mockUser?.avatar}
                  alt={mockUser?.name}
                  className="w-[48px] h-[48px] rounded-full object-cover"
                />
                {mockUser?.isVerified && (
                  <div className="absolute -bottom-[2px] -right-[2px] w-[18px] h-[18px] bg-success rounded-full border-2 border-card flex items-center justify-center">
                    <Icon name="Check" size={12} color="white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{mockUser?.name}</p>
                <p className="text-[13px] text-muted-foreground truncate">{mockUser?.email}</p>
              </div>
              <Icon name="ExternalLink" size={16} className="text-muted-foreground" />
            </div>
            
            <div className="space-y-[6px]">
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-muted-foreground">Profile Completion</span>
                <span className="font-medium text-foreground">{mockUser?.profileCompletion}%</span>
              </div>
              <div className="h-[6px] bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-success transition-smooth"
                  style={{ width: `${mockUser?.profileCompletion}%` }}
                />
              </div>
            </div>
          </Link>

          <div className="py-[6px]">
            {menuItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-between px-[18px] py-[10px]
                  text-foreground hover:bg-muted transition-smooth"
              >
                <div className="flex items-center gap-[12px]">
                  <Icon name={item?.icon} size={18} />
                  <span className="font-medium text-[14px]">{item?.label}</span>
                </div>
                {item?.badge && (
                  <span className="px-[6px] py-[1px] bg-accent text-accent-foreground text-[11px] font-medium rounded-full">
                    {item?.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          <div className="p-[6px] border-t border-border">
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-[12px] px-[18px] py-[10px] rounded-md
                text-error hover:bg-error/10 transition-smooth font-medium text-[14px]"
            >
              <Icon name="LogOut" size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;